class TfFbtBundle extends HTMLElement {
  connectedCallback() {
    this.moneyFormat = this.getAttribute('data-money-format') || null;
    this.cartDrawerSelector = this.getAttribute('data-cart-drawer') || 'cart-drawer';
    this.selectLabel = this.getAttribute('data-select-label') || 'Select material and size';
    this.errorMessage = this.getAttribute('data-error-message') || 'Please select material and size for each item before adding to bag.';
    this.emptyMessage = this.getAttribute('data-empty-message') || 'Please select at least one item to add to bag.';

    this.items = Array.from(this.querySelectorAll('[data-bundle-item]'));
    this.totalEl = this.querySelector('[data-bundle-total]');
    this.addBtn = this.querySelector('[data-bundle-add]');
    this.errorEl = this.querySelector('[data-bundle-error]');

    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-item-check]')) {
        this.onItemChange(e);
      }
    });

    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-bundle-add]');
      if (btn) {
        e.preventDefault();
        this.addBundleToCart();
      }
    });

    // The "Select material and size" popups are <modal-dialog> elements --
    // they relocate themselves to document.body on connect (see
    // ModalDialog in global.js), so by the time their Apply button is
    // clicked they're no longer inside this element. Listen on the
    // document instead and use modal.openedBy (set by <modal-opener>) to
    // find the bundle item the popup belongs to.
    this._onApplyClick = (e) => {
      const applyBtn = e.target.closest('[data-bundle-select-apply]');
      if (!applyBtn) return;

      const modalEl = applyBtn.closest('modal-dialog');
      const opener = modalEl && modalEl.openedBy;
      const li = opener ? opener.closest('[data-bundle-item]') : null;
      if (!li || !this.items.includes(li)) return;

      this.applySelection(li, modalEl);
      if (typeof modalEl.hide === 'function') modalEl.hide();
    };
    document.addEventListener('click', this._onApplyClick);

    // initial total
    this.updateTotal();
  }

  disconnectedCallback() {
    if (this._onApplyClick) {
      document.removeEventListener('click', this._onApplyClick);
    }
  }

  applySelection(li, modalEl) {
    const labelParts = [];

    // Jewelry material / other real variant options (if this popup has one)
    const variantInput = modalEl.querySelector('input[name="id"]');
    if (variantInput) {
      const variantId = Number(variantInput.value);
      const variantsScript = modalEl.querySelector('script[data-variants]');
      let variant = null;

      if (variantsScript) {
        try {
          const variants = JSON.parse(variantsScript.textContent || '[]');
          variant = variants.find((x) => Number(x.id) === variantId) || null;
        } catch {}
      }

      if (variant) {
        li.dataset.variantId = String(variant.id);
        li.dataset.price = String(variant.price);
        li.dataset.compare = String(variant.compare_at_price || 0);

        const priceEl = li.querySelector('[data-item-price]');
        const compareEl = li.querySelector('[data-item-compare]');

        if (priceEl) priceEl.textContent = this.formatMoney(variant.price);

        if (compareEl) {
          if (variant.compare_at_price > variant.price) {
            compareEl.style.display = '';
            compareEl.textContent = this.formatMoney(variant.compare_at_price);
          } else {
            compareEl.style.display = 'none';
          }
        }

        if (variant.title) labelParts.push(variant.title);
      }
    }

    // Ring size (if this popup has one)
    const ringInput = modalEl.querySelector('[data-ring-size-custom-property]');
    if (ringInput && ringInput.value) {
      li.dataset.ringSize = ringInput.value;
      labelParts.push('Size ' + ringInput.value);
    }

    const labelEl = li.querySelector('[data-bundle-select-label]');
    if (labelEl) {
      labelEl.textContent = labelParts.length ? labelParts.join(' · ') : this.selectLabel;
    }

    li.dataset.selected = 'true';
    li.querySelector('[data-bundle-select-toggle]')?.classList.remove('has-error');
    this.clearSelectionError();

    this.updateTotal();
  }

  showSelectionError(unresolved) {
    unresolved.forEach((li) => {
      li.querySelector('[data-bundle-select-toggle]')?.classList.add('has-error');
    });

    if (this.errorEl) {
      this.errorEl.textContent = this.errorMessage;
      this.errorEl.style.display = '';
    }

    unresolved[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  clearSelectionError() {
    if (this.errorEl) this.errorEl.style.display = 'none';
  }

  formatMoney(cents) {
    const fmt = this.getAttribute('data-money-format') || '{{amount}}';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, fmt);
    }

    const value = Number(cents) || 0;

    const formatWithDelimiters = (amountCents, precision, thousands, decimal) => {
      const number = (amountCents / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      const centsPart = parts[1] ? (decimal + parts[1]) : '';
      return dollars + centsPart;
    };

    let token = 'amount';
    const m = fmt.match(/\{\{\s*(amount|amount_no_decimals|amount_with_comma_separator|amount_no_decimals_with_comma_separator)\s*\}\}/);
    if (m && m[1]) token = m[1];

    let formattedNumber = '';
    switch (token) {
      case 'amount':
        formattedNumber = formatWithDelimiters(value, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        formattedNumber = formatWithDelimiters(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formattedNumber = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formattedNumber = formatWithDelimiters(value, 0, '.', ',');
        break;
      default:
        formattedNumber = formatWithDelimiters(value, 2, ',', '.');
    }

    if (!m) return formattedNumber;
    return fmt.replace(m[0], formattedNumber);
  }

  onItemChange(e) {
    this.updateTotal();
  }

  isIncluded(li) {
    const checkbox = li.querySelector('[data-item-check]');
    if (!checkbox) return true;
    return checkbox.checked;
  }

  updateTotal() {
    let total = 0;

    this.items.forEach((li) => {
      if (!this.isIncluded(li)) return;
      total += Number(li.dataset.price || 0);
    });

    if (this.totalEl) this.totalEl.textContent = this.formatMoney(total);
  }

  getCartDrawer() {
    return document.querySelector(this.cartDrawerSelector);
  }

  async refreshAndOpenDrawer() {
    const drawer = this.getCartDrawer();
    if (!drawer) return;

    const CART_URL = window.routes?.cart_url || '/cart';
    const res = await fetch(`${CART_URL}?section_id=cart-drawer`);
    if (!res.ok) return;

    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newDrawer = doc.querySelector('cart-drawer');

    if (newDrawer) drawer.innerHTML = newDrawer.innerHTML;

    requestAnimationFrame(() => drawer.open?.());
  }

  async updateCartCount() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      const bubble = document.querySelector('.cart-count-bubble span');
      if (bubble) bubble.textContent = cart.item_count;
    } catch {}
  }

  async addBundleToCart() {
    const included = this.items.filter(li => this.isIncluded(li));

    if (!included.length) {
      if (this.errorEl) {
        this.errorEl.textContent = this.emptyMessage;
        this.errorEl.style.display = '';
      }
      return;
    }

    const unresolved = included.filter(
      li => li.dataset.needsSelection === 'true' && li.dataset.selected !== 'true'
    );

    if (unresolved.length) {
      this.showSelectionError(unresolved);
      return;
    }

    this.clearSelectionError();

    const items = included
      .map(li => {
        const item = { id: Number(li.dataset.variantId), quantity: 1 };
        if (li.dataset.ringSize) {
          item.properties = { 'Ring Size': li.dataset.ringSize };
        }
        return item;
      })
      .filter(x => Number.isFinite(x.id) && x.id > 0);

    if (!items.length) return;

    this.addBtn?.classList.add('is-loading');

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!res.ok) {
        console.error(await res.text());
        return;
      }

      await this.updateCartCount();
      await this.refreshAndOpenDrawer();
    } catch (e) {
      console.error('[tf-fbt-bundle] add error', e);
    } finally {
      this.addBtn?.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('tf-fbt-bundle')) {
  customElements.define('tf-fbt-bundle', TfFbtBundle);
}
